"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, UserCheck, Clock, ArrowRight, Loader, Calendar, Sparkles, Mail, Phone } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefits = [{
  title: "100% Direct Access to Me",
  description: "No account managers, junior staff, or middle agency layers. You work directly with Yassine Essaadi."
}, {
  title: "Tailored SEO Strategy",
  description: "Custom roadmaps designed for your specific industry, growth goals, and search footprint."
}, {
  title: "Transparent & Honest Metrics",
  description: "Clear deliverables, realistic timelines, and actionable reporting focused on genuine commercial impact."
}, {
  title: "Zero Long-Term Lock-Ins",
  description: "Flexible, outcome-oriented consulting built on earned trust, proven performance, and continuous value."
}];
function InnerConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center shadow-md">
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-accent/20 text-accent">
          <CheckCircle2 className="size-8 text-accent" />
        </div>
        <h3 className="text-xl font-bold text-card-foreground">Consultation Request Received</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage || "Thank you! I will personally review your website details and reply within 24 hours."}
        </p>
        <div className="mt-6 flex justify-center">
          <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted">
            Submit Another Request
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Your full name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background border-input text-foreground focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@company.com" className="bg-background border-input text-foreground focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://yourwebsite.com" className="bg-background border-input text-foreground focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="mt-2 w-full bg-accent text-accent-foreground hover:bg-accent/90 h-11 text-base font-semibold shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Booking Your Strategy Session...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>

      <p className="pt-2 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <ShieldCheck className="size-3.5 text-primary" />
        No pressure sales calls. Just direct, actionable SEO expert advice.
      </p>
    </Form>;
}
export default function ConsultationCTA() {
  return <section id="consultationcta" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24">
      {/* Background Glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Content Column */}
            <motion.div className="lg:col-span-7 space-y-8" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }}>
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-semibold text-secondary-foreground border border-border">
                <Sparkles className="size-3.5 text-accent" />
                <span>Direct Freelance SEO Consulting</span>
              </div>

              <div className="space-y-4">
                <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl font-serif">
                  Ready to Grow Your Search Visibility with a Proven Expert?
                </h2>
                <p className="text-base text-muted-foreground md:text-lg leading-relaxed">
                  Book a free 30-minute discovery consultation directly with me. We will discuss your current SEO benchmarks, identify quick growth opportunities, and establish a practical roadmap for sustainable Google and AI search performance.
                </p>
              </div>

              {/* Direct Consultant Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {benefits.map((benefit, i) => <div data-index={i} key={i} className="flex gap-3 rounded-lg border border-border bg-card p-4 shadow-xs">
                    <CheckCircle2 className="size-5 shrink-0 text-accent mt-0.5" />
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold text-card-foreground">{benefit.title}</h3>
                      <p className="text-xs text-muted-foreground leading-normal">{benefit.description}</p>
                    </div>
                  </div>)}
              </div>

              {/* Founder Trust Callout */}
              <div className="rounded-xl border border-border bg-muted/60 p-5 flex items-start gap-4">
                <div className="relative shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="size-14 rounded-full object-cover border-2 border-primary shadow-xs" loading="lazy" />
                  <span className="absolute bottom-0 right-0 size-3.5 rounded-full bg-emerald-500 border-2 border-background" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground italic">
                    {"\"When you reach out, you speak directly to me, Yassine Essaadi. I personally audit your site and craft your strategy to ensure maximum impact.\""}
                  </p>
                  <p className="text-xs font-semibold text-primary">
                    Yassine Essaadi — Independent SEO Strategy Consultant
                  </p>
                </div>
              </div>

              {/* Direct Contact Options */}
              <div className="flex flex-wrap items-center gap-6 pt-2 text-xs text-muted-foreground">
                <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Mail className="size-4 text-primary" />
                  <span>contact@ye-consultancy.com</span>
                </a>
                <a href="tel:+447490984430" className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Phone className="size-4 text-primary" />
                  <span>+44 7490984430</span>
                </a>
                <Link to="/ContactPage" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline">
                  Or view full contact details
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>

            </motion.div>

            {/* Right Form Card */}
            <motion.div className="lg:col-span-5" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }}>
              <Card className="border-border bg-card shadow-xl relative">
                <CardHeader className="space-y-2 pb-4">
                  <div className="inline-flex items-center gap-1.5 text-xs font-medium text-accent">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <span>Free 30-Min Strategy Session</span>
                  </div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Get Your SEO Strategy Started
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    Fill out the quick form below to request your free initial website review and consultation call.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormProvider formId="consultation-cta-form" sectionName="consultation-cta" defaultValues={{
                  name: "",
                  email: "",
                  website: ""
                }} validationRules={{
                  name: {
                    required: true,
                    minLength: 2
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  website: {
                    required: true
                  }
                }} submitText="Book a Free SEO Consultation" successMessage="Thank you! I will review your website and contact you shortly.">
                    <InnerConsultationForm />
                  </FormProvider>
                </CardContent>
              </Card>
            </motion.div>

          </div>
        </div>
      </div>
    </section>;
}